import {
  DECLINE_REASON_LABELS,
  DeclineReason,
  OkResponse,
  Quote,
  type QuoteItem,
  formatBRL,
  formatBps,
  formatDateTime,
  formatIsoDate,
  addDaysIso,
  isoDateInSaoPaulo,
  quoteReplyValues,
} from '@deizi/shared';
import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { z } from 'zod';
import { Field } from '../../components/Form';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/Overlays';
import { PageHead } from '../../components/PageHead';
import { ErrorBox } from '../../components/States';
import { useToast } from '../../components/Toast';
import { ApiClientError, api } from '../../lib/api';
import { useChannels, useTemplates } from '../../lib/catalog-api';
import { fileContentUrl, uploadFile } from '../../lib/files-api';
import { errorMessage } from '../../lib/messages';
import {
  centavosInput,
  formatGrams,
  formatHm,
  parseBrDate,
  parseCentavos,
} from '../../lib/numbers';
import { useInvalidateQuotes, useQuote } from '../../lib/quotes-api';
import { initials, useIsAdmin } from '../../lib/session';
import { useSettings } from '../../lib/settings-api';
import { useIsPhone } from '../../lib/use-media';
import { CatalogDialog } from '../products/CatalogDialog';
import { ReplyDialog } from '../quote/ReplyDialog';
import { STATUS_BADGE, isOpen, statusLabel } from './format';
import { ConvertDialog, DeclineDialog, useQuoteAction } from './quote-actions';
import { DepositCard, NewRevisionDialog, PdfDialog, RevisionsCard } from './quote-extras';
import { Combobox } from '../../components/Combobox';
import { channelOption } from '../../components/pickers';

/** The bits of the frozen snapshot shown in "Custos congelados" (read defensively). */
const SnapshotView = z.object({
  calculatedAt: z.string(),
  input: z.object({
    energy: z.object({ tariffPerKwh: z.number(), bandeiraPerKwh: z.number() }),
    plates: z.array(
      z.object({
        slots: z.array(z.object({ label: z.string().optional(), pricePerKgCentavos: z.number() })),
      }),
    ),
  }),
  result: z.object({ machinePerHourCentavos: z.number() }),
});

function frozenNote(item: QuoteItem | undefined): string | null {
  const s = SnapshotView.safeParse(item?.snapshot);
  if (!s.success) return null;
  const slots = new Map<string, number>();
  for (const p of s.data.input.plates)
    for (const slot of p.slots) slots.set(slot.label ?? 'filamento', slot.pricePerKgCentavos);
  const filaments = [...slots]
    .map(([label, price]) => `${label} a ${formatBRL(price)}/kg`)
    .join(', ');
  const energy = s.data.input.energy.tariffPerKwh + s.data.input.energy.bandeiraPerKwh;
  return `Calculado em ${formatDateTime(s.data.calculatedAt)} com ${filaments}, energia ${energy.toLocaleString('pt-BR', { maximumFractionDigits: 5 })} R$/kWh e máquina a ${formatBRL(s.data.result.machinePerHourCentavos)}/h. Mudanças de preço depois disso não alteram este orçamento.`;
}

const EVENT_LABELS: Record<string, string> = {
  created: 'Criado',
  sent: 'Enviado ao cliente',
  accepted: 'Aceito',
  declined: 'Recusado',
  expired: 'Marcado como expirado',
  reopened: 'Reaberto como rascunho',
  updated: 'Editado',
  item_added: 'Item adicionado',
  item_updated: 'Item alterado',
  item_removed: 'Item removido',
  attachment_added: 'Referência anexada',
  attachment_removed: 'Referência removida',
  revision: 'Nova revisão',
  deposit_paid: 'Sinal recebido',
  deposit_unpaid: 'Sinal desmarcado',
};

function eventText(e: Quote['events'][number]): string {
  const parsed = z.record(z.string(), z.unknown()).safeParse(e.detail);
  const d = parsed.success ? parsed.data : {};
  const label = EVENT_LABELS[e.type] ?? e.type;
  if (e.type === 'declined' && typeof d.reason === 'string') {
    const r = DeclineReason.safeParse(d.reason);
    return `${label} · ${r.success ? DECLINE_REASON_LABELS[r.data] : d.reason}`;
  }
  if (e.type === 'reopened' && typeof d.validUntil === 'string')
    return `${label} até ${formatIsoDate(d.validUntil)}`;
  if (e.type === 'revision' && typeof d.revision === 'number')
    return `v${d.revision} criada${typeof d.note === 'string' ? `: ${d.note}` : ''}`;
  if (e.type === 'sent' && typeof d.revision === 'number' && d.revision > 1)
    return `v${d.revision} enviada ao cliente`;
  if (e.type.startsWith('deposit_') && typeof d.centavos === 'number')
    return `${label} · ${formatBRL(d.centavos)}`;
  if (typeof d.description === 'string') return `${label}: ${d.description}`;
  if (typeof d.name === 'string') return `${label}: ${d.name}`;
  return label;
}

function useQuoteMutation<T>(id: string, fn: (body: T) => Promise<unknown>) {
  const invalidate = useInvalidateQuotes();
  const toast = useToast();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => invalidate(),
    onError: (err) => {
      // A price below the minimum margin is handled by the item row (asks for the reason).
      if (
        !JSON.stringify(err instanceof ApiClientError ? (err.details ?? '') : '').includes(
          'belowMinimumReason',
        )
      )
        toast(errorMessage(err));
    },
    mutationKey: ['quote', id],
  });
}

function ItemRow({ quote, item, editable }: { quote: Quote; item: QuoteItem; editable: boolean }) {
  const [qty, setQty] = useState(String(item.quantity));
  const [price, setPrice] = useState(centavosInput(item.unitPriceCentavos));
  const [askReason, setAskReason] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const patch = useQuoteMutation(quote.id, (body: object) =>
    api.patch(`/quotes/${quote.id}/items/${item.id}`, Quote, body),
  );
  const remove = useQuoteMutation(quote.id, () =>
    api.delete(`/quotes/${quote.id}/items/${item.id}`, Quote),
  );
  const cols = '1fr 70px 96px 110px 110px 44px';
  const commitQty = () => {
    const n = Number(qty);
    if (!Number.isInteger(n) || n < 1) {
      setQty(String(item.quantity));
      return;
    }
    if (n !== item.quantity) patch.mutate({ quantity: n });
  };
  const commitPrice = () => {
    const c = parseCentavos(price);
    if (c === null || c === item.unitPriceCentavos) {
      setPrice(centavosInput(item.unitPriceCentavos));
      return;
    }
    patch.mutate(
      { unitPriceCentavos: c },
      {
        onError: (err) => {
          if (
            err instanceof ApiClientError &&
            JSON.stringify(err.details ?? '').includes('belowMinimumReason')
          )
            setAskReason(c);
        },
      },
    );
  };
  const sub = [
    item.materials.join(' + '),
    `${formatGrams(item.perUnit.grams)} · ${formatHm(item.perUnit.printSeconds)} por unidade`,
    item.priceSource === 'override' ? 'seu preço' : 'preço sugerido',
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <div className="gtr" style={{ gridTemplateColumns: cols }}>
      <div className="row" style={{ gap: 12, minWidth: 0 }}>
        {item.thumbnailFileId ? (
          <img
            className="thumb"
            src={fileContentUrl(item.thumbnailFileId)}
            alt=""
            width={44}
            height={44}
          />
        ) : (
          <div className="ph" style={{ width: 44, height: 44, flex: '0 0 44px' }}>
            peça
          </div>
        )}
        <div style={{ minWidth: 0 }}>
          <p style={{ fontWeight: 500 }}>{item.description}</p>
          <p className="xs muted">{sub}</p>
          {item.state !== 'ok' ? (
            <p
              className="xs"
              style={{ color: item.state === 'below_cost' ? 'var(--err)' : 'var(--warn)' }}
            >
              {item.state === 'below_cost' ? 'Abaixo do custo' : 'Abaixo da margem mínima'}
              {item.belowMinimumReason ? ` · ${item.belowMinimumReason}` : ''}
            </p>
          ) : null}
        </div>
      </div>
      <span className="num">
        {editable ? (
          <input
            className="input"
            style={{ width: 56, height: 36, textAlign: 'right', padding: '0 8px' }}
            aria-label={`Quantidade de ${item.description}`}
            inputMode="numeric"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            onBlur={commitQty}
            onKeyDown={(e) => e.key === 'Enter' && commitQty()}
          />
        ) : (
          item.quantity
        )}
      </span>
      <span className="num">{formatBRL(item.unitCostCentavos)}</span>
      <span className="num">
        {editable ? (
          <input
            className="ovr"
            aria-label={`Preço de ${item.description}`}
            inputMode="decimal"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            onBlur={commitPrice}
            onKeyDown={(e) => e.key === 'Enter' && commitPrice()}
          />
        ) : (
          formatBRL(item.unitPriceCentavos)
        )}
      </span>
      <span
        className="num"
        style={{
          color:
            item.netProfitCentavos < 0
              ? 'var(--err)'
              : item.state === 'ok'
                ? 'var(--ok)'
                : 'var(--warn)',
        }}
      >
        {formatBRL(item.netProfitCentavos * item.quantity)}
      </span>
      {editable && quote.items.length > 1 ? (
        <button
          type="button"
          className="btn icon g sm"
          style={{ width: 36 }}
          aria-label={`Remover ${item.description}`}
          onClick={() => remove.mutate(undefined)}
        >
          <Icon name="close" size={16} />
        </button>
      ) : (
        <span />
      )}
      {askReason !== null ? (
        <Modal
          title="Salvar com preço abaixo do mínimo?"
          onClose={() => {
            setAskReason(null);
            setPrice(centavosInput(item.unitPriceCentavos));
          }}
          footer={
            <>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setAskReason(null);
                  setPrice(centavosInput(item.unitPriceCentavos));
                }}
              >
                Voltar e ajustar
              </button>
              <button
                type="button"
                className="btn p"
                disabled={reason.trim().length < 3}
                onClick={() =>
                  patch.mutate(
                    { unitPriceCentavos: askReason, belowMinimumReason: reason.trim() },
                    { onSuccess: () => setAskReason(null) },
                  )
                }
              >
                Salvar mesmo assim
              </button>
            </>
          }
        >
          <p className="sm muted">
            A {formatBRL(askReason)}, {item.description} fica abaixo da margem mínima.
          </p>
          <Field
            id="why"
            label="Motivo (obrigatório)"
            help="Fica registrado no histórico do orçamento."
          >
            <textarea
              id="why"
              className="input"
              rows={3}
              placeholder="Ex.: cliente antiga, compra em volume…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
        </Modal>
      ) : null}
    </div>
  );
}

export function QuoteDetailPage() {
  const { id = '' } = useParams();
  const quote = useQuote(id);
  if (quote.error && !quote.data) {
    return (
      <>
        <PageHead crumb="Orçamentos" title="Orçamento" />
        <div className="card">
          <ErrorBox
            title={
              quote.error instanceof ApiClientError && quote.error.status === 404
                ? 'Orçamento não encontrado'
                : 'Não foi possível carregar o orçamento'
            }
            onRetry={() => void quote.refetch()}
          />
        </div>
      </>
    );
  }
  if (!quote.data) return <div className="skel" style={{ height: 420 }} />;
  return <Detail key={quote.data.id} quote={quote.data} />;
}

function Detail({ quote }: { quote: Quote }) {
  const phone = useIsPhone();
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const toast = useToast();
  const invalidate = useInvalidateQuotes();
  const channels = useChannels();
  const templates = useTemplates();
  const action = useQuoteAction();
  const [dialog, setDialog] = useState<
    'decline' | 'reply' | 'delete' | 'validity' | 'add' | 'convert' | 'pdf' | 'revision' | null
  >(null);
  const [notes, setNotes] = useState<string | null>(null);
  const [validity, setValidity] = useState(formatIsoDate(quote.validUntil));
  const fileInput = useRef<HTMLInputElement>(null);
  const settings = useSettings();
  const openValidity = () => {
    setValidity(
      formatIsoDate(
        editable
          ? quote.validUntil
          : addDaysIso(isoDateInSaoPaulo(), settings.data?.rules.quoteValidityDays ?? 7),
      ),
    );
    setDialog('validity');
  };
  const editable = isOpen(quote.status);
  const run = (a: Parameters<typeof action.mutate>[0]['action']) =>
    action.mutate({ id: quote.id, action: a });

  const patch = useQuoteMutation(quote.id, (body: object) =>
    api.patch(`/quotes/${quote.id}`, Quote, body),
  );
  const attach = useQuoteMutation(quote.id, async (file: File) => {
    const stored = await uploadFile(file, 'reference');
    return api.post(`/quotes/${quote.id}/attachments`, Quote, { fileId: stored.id });
  });
  const addItem = useQuoteMutation(quote.id, (variantId: string) =>
    api.post(
      `/quotes/${quote.id}/items`,
      Quote,
      { variantId },
      { idempotencyKey: crypto.randomUUID() },
    ),
  );
  const detach = useQuoteMutation(quote.id, (fileId: string) =>
    api.delete(`/quotes/${quote.id}/attachments/${fileId}`, Quote),
  );
  const remove = useMutation({
    mutationFn: () => api.delete(`/quotes/${quote.id}`, OkResponse),
    onSuccess: () => {
      invalidate();
      toast(`${quote.number} foi para a lixeira`);
      void navigate('/orcamentos');
    },
    onError: (err) => toast(errorMessage(err)),
  });

  const title = quote.items[0]?.description ?? quote.number;
  const totalCost = quote.items.reduce((n, i) => n + i.unitCostCentavos * i.quantity, 0);
  const marketplace =
    (channels.data?.data.find((c) => c.id === quote.channel?.id)?.current?.commissionBps ?? 0) > 0;
  const days = quote.daysUntilExpiry;

  const alert = (() => {
    switch (quote.status) {
      case 'draft':
        return (
          <div className="alert">
            <Icon name="quote" />
            <div className="grow">
              <b>Rascunho v{quote.revision}.</b> Ainda não foi para o cliente. Gere o PDF ou copie a
              resposta e marque como enviado quando mandar.
            </div>
            <button type="button" className="btn sm" onClick={() => run('send')}>
              Marcar como enviado
            </button>
          </div>
        );
      case 'sent':
        return (
          <div className={days <= 1 ? 'alert warn' : 'alert'}>
            <Icon name="alert" />
            <div className="grow">
              <b>
                Válido {days === 0 ? 'até hoje' : days === 1 ? 'até amanhã' : 'até'},{' '}
                {formatIsoDate(quote.validUntil)}.
              </b>{' '}
              {quote.sentAt ? `Enviado em ${formatDateTime(quote.sentAt)}.` : ''} Depois disso, vira
              Expirado sozinho.
            </div>
            <button type="button" className="btn sm" onClick={() => setDialog('reply')}>
              Copiar resposta
            </button>
          </div>
        );
      case 'accepted':
        return (
          <div className="alert ok">
            <Icon name="check" />
            <div className="grow">
              <b>Aceito{quote.decidedAt ? ` em ${formatDateTime(quote.decidedAt)}` : ''}.</b>{' '}
              Converta em pedido para reservar a pronta entrega e mandar o resto para a fila.
            </div>
          </div>
        );
      case 'declined':
        return (
          <div className="alert err">
            <Icon name="alert" />
            <div className="grow">
              <b>
                Recusado ·{' '}
                {quote.declineReason ? DECLINE_REASON_LABELS[quote.declineReason] : 'sem motivo'}.
              </b>{' '}
              {quote.declineNote ?? ''}
            </div>
          </div>
        );
      case 'expired':
        return (
          <div className="alert warn">
            <Icon name="alert" />
            <div className="grow">
              <b>Expirado.</b> O cliente não respondeu dentro da validade. Reabra para mandar de
              novo; para custos de hoje, refaça no orçamento rápido.
            </div>
          </div>
        );
      default:
        return null;
    }
  })();

  const pipeline: { key: string; label: string; cls: string }[] = (() => {
    const order = ['draft', 'sent', 'decided', 'converted'] as const;
    const at =
      quote.status === 'draft'
        ? 0
        : quote.status === 'sent'
          ? 1
          : quote.status === 'converted'
            ? 3
            : 2;
    return order.map((k, i) => ({
      key: k,
      label:
        k === 'decided'
          ? at >= 2 && quote.status !== 'converted'
            ? statusLabel(quote.status)
            : 'Aceito ou recusado'
          : statusLabel(k),
      cls:
        i === at ? 'badge solid' : i < at && (k !== 'sent' || quote.sentAt) ? 'badge ok' : 'badge',
    }));
  })();

  const revisionBadges = (
    <>
      {quote.revision > 1 ? <span className="badge">v{quote.revision}</span> : null}
      {quote.deposit?.paidAt ? (
        <span className="badge ok">
          <Icon name="check" size={14} />
          Sinal recebido
        </span>
      ) : null}
    </>
  );

  const actions = (
    <div className="row wrap" style={{ gap: 10 }}>
      <button type="button" className="btn" onClick={() => setDialog('pdf')}>
        <Icon name="quote" size={18} />
        PDF para o cliente
      </button>
      <button type="button" className="btn" onClick={() => setDialog('reply')}>
        <Icon name="copy" size={18} />
        Copiar resposta
      </button>
      {quote.status !== 'converted' ? (
        <button type="button" className="btn" onClick={() => setDialog('revision')}>
          Nova revisão
        </button>
      ) : null}
      {quote.status === 'draft' ? (
        <button type="button" className="btn p" onClick={() => run('send')}>
          Marcar como enviado
        </button>
      ) : null}
      {editable ? (
        <>
          <button type="button" className="btn g" onClick={() => run('expire')}>
            Marcar expirado
          </button>
          <button type="button" className="btn d" onClick={() => setDialog('decline')}>
            Recusado
          </button>
          <button
            type="button"
            className={quote.status === 'sent' ? 'btn p' : 'btn'}
            onClick={() => run('accept')}
          >
            Aceito
          </button>
        </>
      ) : null}
      {quote.status === 'accepted' ? (
        <button type="button" className="btn p" onClick={() => setDialog('convert')}>
          Converter em pedido
        </button>
      ) : null}
      {quote.status === 'converted' && quote.order ? (
        <Link className="btn p" to={`/pedidos/${quote.order.id}`}>
          Ver pedido {quote.order.number}
        </Link>
      ) : null}
      {quote.status === 'declined' || quote.status === 'expired' ? (
        <button type="button" className="btn p" onClick={openValidity}>
          Reabrir como rascunho
        </button>
      ) : null}
    </div>
  );

  return (
    <>
      {phone ? (
        <div className="col" style={{ gap: 6 }}>
          <p className="xs muted">
            <Link to="/orcamentos">Orçamentos</Link> / {quote.number}
          </p>
          <div className="row wrap" style={{ gap: 8 }}>
            <h1 style={{ fontSize: 22 }}>{title}</h1>
            <span className={STATUS_BADGE[quote.status]}>{statusLabel(quote.status)}</span>
            {revisionBadges}
          </div>
          {actions}
        </div>
      ) : (
        <div className="pagehead">
          <div>
            <div className="crumb">
              <Link to="/orcamentos">Orçamentos</Link> / {quote.number}
            </div>
            <div className="row wrap" style={{ gap: 12 }}>
              <h1>{title}</h1>
              <span className={STATUS_BADGE[quote.status]}>{statusLabel(quote.status)}</span>
              {revisionBadges}
            </div>
          </div>
          {actions}
        </div>
      )}
      {alert}
      <div className="row" style={{ gap: 6, flexWrap: 'wrap' }} aria-label="Ciclo do orçamento">
        {pipeline.map((p, i) => (
          <span key={p.key} className="row" style={{ gap: 6 }}>
            <span className={p.cls}>{p.label}</span>
            {i < pipeline.length - 1 ? (
              <span className="muted" aria-hidden="true">
                ›
              </span>
            ) : null}
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'flex-start' }}>
        <div className="col" style={{ flex: '2 1 520px', minWidth: 0, gap: 18 }}>
          <section className="card flat" style={{ overflowX: 'auto' }}>
            <div className="row between wrap" style={{ padding: '16px 18px 6px', gap: 12 }}>
              <h2>Itens</h2>
              <div className="row" style={{ gap: 12 }}>
                {editable ? (
                  <div className="row sm" style={{ gap: 8 }}>
                    <span className="muted">Canal</span>
                    <Combobox
                      size="sm"
                      ariaLabel="Canal"
                      style={{ width: 190 }}
                      value={quote.channel?.id ?? ''}
                      options={(channels.data?.data ?? [])
                        .filter((c) => c.active || c.id === quote.channel?.id)
                        .map(channelOption)}
                      onChange={(v) => patch.mutate({ channelId: v })}
                    />
                  </div>
                ) : (
                  <span className="sm muted">Canal: {quote.channel?.name ?? '—'}</span>
                )}
                {editable ? (
                  <button type="button" className="btn sm" onClick={() => setDialog('add')}>
                    + Item
                  </button>
                ) : null}
                <Link
                  className="btn sm g"
                  to="/orcamento-rapido"
                  title="Calcule no orçamento rápido e salve como novo orçamento"
                >
                  Novo cálculo
                </Link>
              </div>
            </div>
            <div className="gt" style={{ minWidth: 640 }}>
              <div
                className="gtr head"
                style={{ gridTemplateColumns: '1fr 70px 96px 110px 110px 44px' }}
              >
                <span>Item</span>
                <span className="num">Qtd.</span>
                <span className="num">Custo un.</span>
                <span className="num">Preço un.</span>
                <span className="num">Lucro</span>
                <span />
              </div>
              {quote.items.map((it) => (
                <ItemRow
                  key={`${it.id}-${it.quantity}-${it.unitPriceCentavos}`}
                  quote={quote}
                  item={it}
                  editable={editable}
                />
              ))}
              <div
                className="gtr foot"
                style={{ gridTemplateColumns: '1fr 70px 96px 110px 110px 44px' }}
              >
                <span>Total</span>
                <span />
                <span />
                <span className="num">{formatBRL(quote.totals.priceCentavos)}</span>
                <span className="num">
                  {formatBRL(quote.totals.profitCentavos)} · {formatBps(quote.totals.marginBps)}
                </span>
                <span />
              </div>
            </div>
            <p className="xs muted" style={{ padding: '10px 18px 14px' }}>
              Custo total {formatBRL(totalCost)} · impressão{' '}
              {formatHm(quote.totals.totalPrintSeconds)} · pronto em cerca de {quote.leadTimeDays}{' '}
              {quote.leadTimeDays === 1 ? 'dia' : 'dias'}.
            </p>
          </section>

          <DepositCard key={`${quote.deposit?.bps ?? 0}`} quote={quote} />

          <section className="card col" style={{ gap: 12 }}>
            <div className="row between">
              <h2>Referências do cliente</h2>
              <button
                type="button"
                className="btn sm"
                onClick={() => fileInput.current?.click()}
                disabled={attach.isPending}
              >
                {attach.isPending ? 'Enviando…' : '+ Anexar'}
              </button>
              <input
                ref={fileInput}
                type="file"
                hidden
                accept="image/*,.pdf,.3mf,.stl"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) attach.mutate(f, { onSuccess: () => toast(`${f.name} anexado`) });
                }}
              />
            </div>
            {quote.attachments.length === 0 ? (
              <p className="sm muted">
                Fotos, desenhos ou PDFs que o cliente mandou. Até 25 MB por arquivo.
              </p>
            ) : (
              <div className="row wrap" style={{ gap: 12 }}>
                {quote.attachments.map((f) =>
                  f.mime.startsWith('image/') ? (
                    <div key={f.id} className="col" style={{ gap: 4, width: 120 }}>
                      <a href={fileContentUrl(f.id)} target="_blank" rel="noreferrer">
                        <img
                          className="thumb"
                          src={fileContentUrl(f.id)}
                          alt={f.name}
                          width={120}
                          height={96}
                        />
                      </a>
                      <button
                        type="button"
                        className="btn sm g"
                        style={{ padding: 0 }}
                        onClick={() => detach.mutate(f.id)}
                      >
                        Remover
                      </button>
                    </div>
                  ) : (
                    <div key={f.id} className="fchip">
                      <Icon name="image" size={20} />
                      <div className="grow" style={{ minWidth: 0 }}>
                        <a
                          className="trunc sm"
                          style={{ fontWeight: 500, display: 'block' }}
                          href={fileContentUrl(f.id)}
                          target="_blank"
                          rel="noreferrer"
                          title={f.name}
                        >
                          {f.name}
                        </a>
                        <p className="xs muted">{Math.max(1, Math.round(f.sizeBytes / 1024))} KB</p>
                      </div>
                      <button
                        type="button"
                        className="btn icon sm g"
                        aria-label={`Remover ${f.name}`}
                        onClick={() => detach.mutate(f.id)}
                      >
                        <Icon name="close" size={16} />
                      </button>
                    </div>
                  ),
                )}
              </div>
            )}
          </section>

          <section className="card col" style={{ gap: 10 }}>
            <div className="row between">
              <h2>Observações</h2>
              {notes === null ? (
                <button
                  type="button"
                  className="btn sm"
                  onClick={() => setNotes(quote.notes ?? '')}
                >
                  Editar
                </button>
              ) : null}
            </div>
            {notes === null ? (
              <p
                className={quote.notes ? undefined : 'sm muted'}
                style={{ whiteSpace: 'pre-line' }}
              >
                {quote.notes ?? 'Sem observações.'}
              </p>
            ) : (
              <>
                <textarea
                  className="input"
                  aria-label="Observações"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
                <div className="row" style={{ gap: 10, justifyContent: 'flex-end' }}>
                  <button type="button" className="btn sm" onClick={() => setNotes(null)}>
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn sm p"
                    onClick={() =>
                      patch.mutate(
                        { notes: notes.trim() || null },
                        { onSuccess: () => setNotes(null) },
                      )
                    }
                  >
                    Salvar
                  </button>
                </div>
              </>
            )}
          </section>
        </div>

        <aside className="col" style={{ flex: '1 1 300px', minWidth: 0, gap: 18 }}>
          <section className="card col" style={{ gap: 10 }}>
            <h3>Cliente</h3>
            {quote.customer ? (
              <>
                <div className="row" style={{ gap: 10 }}>
                  <span className="avatar co">{initials(quote.customer.name)}</span>
                  <div>
                    <p style={{ fontWeight: 500 }}>{quote.customer.name}</p>
                    {quote.customer.whatsapp ? (
                      <p className="sm muted">WhatsApp {quote.customer.whatsapp}</p>
                    ) : null}
                  </div>
                </div>
                <Link to={`/clientes?id=${quote.customer.id}`} className="sm">
                  Ver cliente e histórico
                </Link>
              </>
            ) : (
              <p className="sm muted">Sem cliente.</p>
            )}
          </section>
          <section className="card col" style={{ gap: 6 }}>
            <div className="row between">
              <h3>Validade</h3>
              {editable ? (
                <button type="button" className="btn sm" onClick={openValidity}>
                  Alterar
                </button>
              ) : null}
            </div>
            <p className="tab">
              {formatIsoDate(quote.validUntil)}
              {editable
                ? days >= 0
                  ? ` · ${days === 0 ? 'hoje' : days === 1 ? 'amanhã' : `em ${days} dias`}`
                  : ' · vencido'
                : ''}
            </p>
          </section>
          <RevisionsCard quote={quote} />
          <section className="card col" style={{ gap: 8 }}>
            <h3>Histórico</h3>
            {quote.events.toReversed().map((e, i) => (
              <div key={`${e.at}-${i}`} className="kv sm">
                <span>{eventText(e)}</span>
                <span className="v muted" title={e.actor}>
                  {formatDateTime(e.at)}
                </span>
              </div>
            ))}
          </section>
          <section className="card sunk col" style={{ gap: 6 }}>
            <h3>Custos congelados</h3>
            <p className="sm muted">
              {frozenNote(quote.items[0]) ??
                'Os custos foram guardados quando o orçamento foi salvo.'}
            </p>
          </section>
          {isAdmin ? (
            <button
              type="button"
              className="btn g sm"
              style={{ alignSelf: 'flex-start', color: 'var(--err)' }}
              onClick={() => setDialog('delete')}
            >
              Excluir orçamento
            </button>
          ) : null}
        </aside>
      </div>

      {dialog === 'pdf' ? <PdfDialog quote={quote} onClose={() => setDialog(null)} /> : null}
      {dialog === 'revision' ? (
        <NewRevisionDialog quote={quote} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === 'decline' ? (
        <DeclineDialog quote={quote} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === 'reply' ? (
        <ReplyDialog
          templates={templates.data?.data ?? []}
          values={quoteReplyValues({
            customerName: quote.customer?.name ?? null,
            items: quote.items,
            leadTimeDays: quote.leadTimeDays,
            validUntil: quote.validUntil,
          })}
          whatsapp={quote.customer?.whatsapp}
          marketplace={marketplace}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === 'convert' ? (
        <ConvertDialog
          quote={{
            ...quote,
            itemsSummary: quote.items
              .map((i) => `${i.description}${i.quantity > 1 ? ` ×${i.quantity}` : ''}`)
              .join(', '),
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === 'add' ? (
        <CatalogDialog
          title="Adicionar item"
          intro={`Preço sugerido para ${quote.channel?.name ?? 'o canal do orçamento'} com os custos e taxas de hoje. Custo do cadastro do produto; quantidade e preço se ajustam na tabela.`}
          channelId={quote.channel?.id ?? null}
          priceLabel={`Preço ${quote.channel?.name ?? ''}`.trim()}
          actionLabel="Adicionar"
          pending={addItem.isPending}
          onClose={() => setDialog(null)}
          onPick={(item) =>
            addItem.mutate(item.variant.id, {
              onSuccess: () => toast(`${item.product.name} ${item.variant.name} adicionado`),
            })
          }
        />
      ) : null}
      {dialog === 'validity' ? (
        <Modal
          title={editable ? 'Alterar validade' : 'Reabrir com nova validade'}
          onClose={() => setDialog(null)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setDialog(null)}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn p"
                onClick={() => {
                  const iso = parseBrDate(validity);
                  if (!iso || iso < isoDateInSaoPaulo()) {
                    toast('Use uma data de hoje em diante, DD/MM/AAAA.');
                    return;
                  }
                  if (editable)
                    patch.mutate({ validUntil: iso }, { onSuccess: () => setDialog(null) });
                  else
                    void api
                      .post(`/quotes/${quote.id}/reopen`, Quote, { validUntil: iso })
                      .then(() => {
                        invalidate();
                        toast(`${quote.number} reaberto como rascunho`);
                        setDialog(null);
                      })
                      .catch((err: unknown) => toast(errorMessage(err)));
                }}
              >
                {editable ? 'Salvar' : 'Reabrir'}
              </button>
            </>
          }
        >
          {editable ? null : (
            <p className="sm muted">
              Volta para rascunho com os mesmos preços (congelados). Mande de novo quando quiser.
            </p>
          )}
          <Field id="val" label="Válido até" help="DD/MM/AAAA">
            <input
              id="val"
              className="input tab"
              inputMode="numeric"
              value={validity}
              onChange={(e) => setValidity(e.target.value)}
            />
          </Field>
        </Modal>
      ) : null}
      {dialog === 'delete' ? (
        <Modal
          title={`Excluir ${quote.number}?`}
          onClose={() => setDialog(null)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setDialog(null)}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn d"
                onClick={() => remove.mutate()}
                disabled={remove.isPending}
              >
                Excluir
              </button>
            </>
          }
        >
          <p className="sm">Vai para a lixeira e pode ser restaurado por 30 dias.</p>
        </Modal>
      ) : null}
    </>
  );
}
